import React, { useEffect } from 'react';
import { Admin, Resource, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import {
  People,
  SecurityOutlined,
  Business,
  Factory,
  Dashboard,
  Analytics,
  AccountTree,
  Alarm,
  DataObject,
  TableChart,
  Room,
  Input as InputIcon,
  Devices as DevicesIcon,
  Assignment,
  Notifications,
  Task,
  Stream as StreamIcon,
  FileCopy,
  NotificationAdd,
  NotificationImportant,
} from '@mui/icons-material';

import {
  ClientsList,
  ClientsCreate,
  ClientsEdit,
  ClientsShow,
} from '@/pages/clients';
import {
  InputsList,
  InputsCreate,
  InputsEdit,
  InputsShow,
} from './pages/inputs';
import {
  DevicesList,
  DevicesCreate,
  DevicesEdit,
  DevicesShow,
} from './pages/devices';
import {
  PlantsList,
  PlantsCreate,
  PlantsEdit,
  PlantsShow,
} from '@/pages/plants';
import {
  DashboardGroupsList,
  DashboardGroupsCreate,
  DashboardGroupsEdit,
  DashboardGroupsShow,
} from '@/pages/dashboardgroups';
import {
  DashboardsList,
  DashboardsCreate,
  DashboardsEdit,
  DashboardsShow,
} from '@/pages/dashboards';
import {
  DiagramsList,
  DiagramsCreate,
  DiagramsEdit,
  DiagramsShow,
} from '@/pages/diagrams';
import {
  AlertsList,
  AlertsCreate,
  AlertsEdit,
  AlertsShow,
} from '@/pages/alerts';
import {
  TicketsList,
  TicketsEdit,
  TicketsShow,
  TicketsCreate,
} from '@/pages/tickets';
import {
  NotificationsList,
  NotificationsShow,
  NotificationsCreate,
  NotificationsEdit,
} from '@/pages/notifications';
import ZonesList from './pages/zones/ZonesList';
import ZonesShow from './pages/zones/ZonesShow';
import ZonesEdit from './pages/zones/ZonesEdit';
import { ZonesCreate } from './pages/zones';
import {
  TablesCreate,
  TablesEdit,
  TablesList,
  TablesShow,
} from './pages/tables';
import VariablesList from './pages/variables/VariablesList';
import VariablesShow from './pages/variables/VariablesShow';
import VariablesEdit from './pages/variables/VariablesEdit';
import VariablesCreate from './pages/variables/VariablesCreate';
import StatisticPage from './pages/statistics/StatisticPage';
import { UsersList } from './pages/users/UsersList';
import { UsersEdit } from './pages/users/UsersEdit';
import { UsersCreate } from './pages/users/UsersCreate';
import { UsersShow } from './pages/users/UsersShow';
import { RolesList } from './pages/roles/RolesList';
import { RolesEdit } from './pages/roles/RolesEdit';
import { RolesCreate } from './pages/roles/RolesCreate';
import { RolesShow } from './pages/roles/RolesShow';
import { dataProvider } from './providers/dataProvider';
import { authProvider } from './providers/authProvider';
import { darkTheme, lightTheme } from './themes/theme';
import { Login } from './components/Login';
import TasksList from './pages/tasks/TasksList';
import { DataStream } from './pages/datastream';
import { CustomLayout } from './components/CustomLayout';
import CsvSchemasList from './pages/csvschemas/CsvSchemasList';
import CsvSchemasShow from './pages/csvschemas/CsvSchemasShow';
import CreateCsvSchema from './pages/csvschemas/CreateCsvSchema';
import CsvSchemasData from './pages/csvschemas/CsvSchemasData';
import TasksShow from './pages/tasks/TasksShow';
import RegisterClientSimple from './pages/register_client/RegisterClient';
import SubscriptionList from './pages/subscription/SubscriptionList';
import SubscriptionShow from './pages/subscription/SubscriptionShow';
import SubscriptionCreate from './pages/subscription/SubscriptionCreate';
import SubscriptionEdit from './pages/subscription/SubscriptionEdit';

const App: React.FC = () => {
  return (
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      title='Inoelec - Panel de Administración'
      darkTheme={darkTheme}
      lightTheme={lightTheme}
      loginPage={Login}
      layout={CustomLayout}
    >
      <Resource
        name='statistics'
        list={StatisticPage}
        options={{ label: 'Estadísticas' }}
      />
      <Resource
        name='users'
        list={UsersList}
        edit={UsersEdit}
        create={UsersCreate}
        show={UsersShow}
        icon={People}
        options={{ label: 'Usuarios' }}
      />{' '}
      <Resource
        name='roles'
        list={RolesList}
        edit={RolesEdit}
        create={RolesCreate}
        show={RolesShow}
        icon={SecurityOutlined}
        options={{ label: 'Roles' }}
      />{' '}
      <Resource
        name='clients'
        list={ClientsList}
        edit={ClientsEdit}
        create={ClientsCreate}
        show={ClientsShow}
        icon={Business}
        options={{ label: 'Clientes' }}
      />{' '}
      <Resource
        name='plants'
        list={PlantsList}
        edit={PlantsEdit}
        create={PlantsCreate}
        show={PlantsShow}
        icon={Factory}
        options={{ label: 'Plantas' }}
      />
      <Resource
        name='dashboardgroups'
        list={DashboardGroupsList}
        edit={DashboardGroupsEdit}
        create={DashboardGroupsCreate}
        show={DashboardGroupsShow}
        icon={Dashboard}
        options={{ label: 'Grupos de Dashboard' }}
      />{' '}
      <Resource
        name='dashboards'
        list={DashboardsList}
        edit={DashboardsEdit}
        create={DashboardsCreate}
        show={DashboardsShow}
        icon={Analytics}
        options={{ label: 'Dashboards' }}
      />
      <Resource
        name='diagrams'
        list={DiagramsList}
        edit={DiagramsEdit}
        create={DiagramsCreate}
        show={DiagramsShow}
        icon={AccountTree}
        options={{ label: 'Diagramas' }}
      />{' '}
      <Resource
        name='alerts'
        list={AlertsList}
        edit={AlertsEdit}
        create={AlertsCreate}
        show={AlertsShow}
        icon={Alarm}
        options={{ label: 'Alertas' }}
      />
      <Resource
        name='tickets-admin'
        list={TicketsList}
        create={TicketsCreate}
        edit={TicketsEdit}
        show={TicketsShow}
        icon={Assignment}
        options={{ label: 'Tickets' }}
      />
      <Resource
        name='notifications-admin'
        list={NotificationsList}
        create={NotificationsCreate}
        edit={NotificationsEdit}
        show={NotificationsShow}
        icon={Notifications}
        options={{ label: 'Notificaciones' }}
      />
      {/* Recursos de referencia para ReferenceField/ReferenceInput */}
      <Resource
        name='zones'
        list={ZonesList}
        show={ZonesShow}
        edit={ZonesEdit}
        create={ZonesCreate}
        icon={Room}
        options={{ label: 'Zonas' }}
      />{' '}
      <Resource
        name='tables'
        list={TablesList}
        edit={TablesEdit}
        show={TablesShow}
        create={TablesCreate}
        icon={TableChart}
        options={{ label: 'Tablas' }}
      />{' '}
      <Resource
        name='variables'
        list={VariablesList}
        show={VariablesShow}
        edit={VariablesEdit}
        create={VariablesCreate}
        icon={DataObject}
        options={{ label: 'Variables Scada' }}
      />
      {/* TODO: TASK */}
      <Resource
        name='task'
        list={TasksList}
        show={TasksShow}
        icon={Task}
        options={{ label: 'Tasks' }}
      />
      <Resource
        name='inputs'
        list={InputsList}
        edit={InputsEdit}
        create={InputsCreate}
        show={InputsShow}
        icon={InputIcon}
        options={{ label: 'Inputs' }}
      />
      <Resource
        name='devices'
        list={DevicesList}
        edit={DevicesEdit}
        create={DevicesCreate}
        show={DevicesShow}
        icon={DevicesIcon}
        options={{ label: 'Devices' }}
      />
      {/* DataStream - Elemento separado */}
      <Resource
        name='datastream'
        list={DataStream}
        icon={StreamIcon}
        options={{ label: 'DataStream' }}
      />
      <Resource
        icon={FileCopy}
        name='CsvSchemas'
        create={CreateCsvSchema}
        show={CsvSchemasShow}
        list={CsvSchemasList}
        options={{ label: 'CsvSchemas' }}
      />
      <Resource
        name='Subscripciones'
        icon={NotificationImportant}
        list={<SubscriptionList />}
        show={<SubscriptionShow />}
        create={<SubscriptionCreate />}
        edit={<SubscriptionEdit />}
      />
      {/* Rutas personalizadas */}
      <CustomRoutes>
        <Route path='/CsvSchemas/:id/data' element={<CsvSchemasData />} />
        <Route path='/register-client' element={<RegisterClientSimple />} />
      </CustomRoutes>
    </Admin>
  );
};

export default App;
